/**
 * What cubic's page and plugin share about the local OpenCode server: where
 * it listens and the password it was started with, the errors a request to it
 * ends in, its deadline, and the session an event belongs to. The page's
 * requests and the event stream go through OpenCode's own client
 * (`opencode-client.ts`), so what they carry is typed by the pinned
 * `@opencode/client`, which generates OpenCode 2.0.26's own types; the plugin's
 * two reads of the running server (`server-process.ts`) use plain `fetch`. This
 * file takes only types from that client, so the modules a plain browser's
 * `/code` loads use it without loading the client.
 * `fetch` is passed in, so tests capture requests instead of a server.
 */
import type { OpenCodeEvent } from '@opencode/client/promise';

/** Where the running server listens and the password it was started with. */
export type OpenCodeServerAddress = {
  url: string;
  password: string;
};

export type FetchLike = typeof globalThis.fetch;

/** The method, and the path with its query, of one request to the local server. */
export type OpenCodeRoute = { method: string; path: string };

/**
 * The subscription `localOpenCode`'s `events` opens, which names its own
 * failures, and its silence when `runEventStream`'s watchdog drops it.
 */
export const EVENT_ROUTE: OpenCodeRoute = { method: 'GET', path: '/api/event' };

/**
 * A failure status from the local server: the route and the status, never
 * the response body, which nothing reads and which could carry what the
 * server was asked.
 */
export class OpenCodeHttpError extends Error {
  constructor(
    readonly request: OpenCodeRoute,
    readonly status: number,
  ) {
    super(`OpenCode ${request.method} ${request.path} failed with ${status}`);
    this.name = 'OpenCodeHttpError';
  }
}

/**
 * How long one request to the local server may take, its body included.
 * OpenCode answers every route `localOpenCode`'s `call` sends within a second,
 * bar the routes below that wait on work; a request still unanswered by then is
 * given up, so a stalled read ends in a failure the page shows with Retry
 * instead of a page that waits forever. The event stream, which OpenCode holds
 * open, is not sent through it.
 */
export const OPENCODE_REQUEST_TIMEOUT_MS = 15_000;

/**
 * How long Undo waits for an interrupted turn to end before staging its revert. The wait answers
 * only once the turn has stopped, which a tool that is slow to give up can delay.
 */
export const OPENCODE_SETTLE_TIMEOUT_MS = 60_000;

/**
 * How long staging or clearing a revert may take. OpenCode restores the files as it answers, at
 * about 14 ms a file, and keeps going after a client gives up, as the cloud's client
 * (`REVERT_TIMEOUT_MS`) allows for.
 */
export const OPENCODE_REVERT_TIMEOUT_MS = 120_000;

/**
 * A request the local server did not finish answering within
 * `OPENCODE_REQUEST_TIMEOUT_MS`: the route and the deadline, as
 * `OpenCodeHttpError` keeps the route.
 */
export class OpenCodeTimeoutError extends Error {
  constructor(
    readonly request: OpenCodeRoute,
    readonly timeoutMs: number,
  ) {
    super(`OpenCode ${request.method} ${request.path} did not answer within ${timeoutMs} ms`);
    this.name = 'OpenCodeTimeoutError';
  }
}

/** The `Authorization` header the server checks: basic auth with the user `opencode`. */
export function basicAuthorization(password: string) {
  return `Basic ${btoa(`opencode:${password}`)}`;
}

/** An event frame from `GET /api/event`, in OpenCode's own type. Session events carry `sessionID` in `data`. */
export type { OpenCodeEvent };

/**
 * The session an event belongs to. Session events carry `sessionID` in
 * `data`; events about a thing the session owns wrap it (`form.created` has
 * `form.sessionID`, `shell.created` has `info.metadata.sessionID`). `pty.*`
 * name none: OpenCode 2.0.26's `Pty` holds no session. Server-wide events have
 * none.
 */
export function sessionIdOf(event: OpenCodeEvent): string | null {
  switch (event.type) {
    case 'form.created':
      return event.data.form.sessionID;
    case 'shell.created': {
      // OpenCode keeps the session that started a shell in its metadata, which it leaves untyped.
      const sessionId: unknown = event.data.info.metadata.sessionID;
      return typeof sessionId === 'string' ? sessionId : null;
    }
    default:
      return 'sessionID' in event.data && typeof event.data.sessionID === 'string'
        ? event.data.sessionID
        : null;
  }
}
